import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Users, Home, FileText, Target, BarChart3, Play, CheckCircle, ArrowRight, DollarSign, Bell, Mail, PenTool, Heart, Search, Briefcase, Undo2, Zap, Phone, Calendar, Globe, Radar } from 'lucide-react';
import { Input } from '@/components/ui/input';

import VisionAireLogo from '@/components/VisionAireLogo';
import { PhoneDialerTab, AppointmentsTab } from '@/components/SignalWireComponents';
import { RelocationLeadsTab } from '@/components/RelocationLeads';
import { LeadRadarTab } from '@/components/LeadRadar';

const STATUS_COLORS = {
    lead: 'bg-zinc-100 text-zinc-600', matched: 'bg-blue-100 text-blue-700',
    negotiation: 'bg-amber-100 text-amber-700', finalized: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-purple-100 text-purple-700',
};

const LISTING_STATUS_COLORS = {
    pending: 'bg-amber-100 text-amber-700',
    active: 'bg-emerald-100 text-emerald-700',
    matched: 'bg-blue-100 text-blue-700',
    closed: 'bg-purple-100 text-purple-700',
};

export default function AdminPage() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [buyers, setBuyers] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [deals, setDeals] = useState([]);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [matchLoading, setMatchLoading] = useState(false);
    const [matchResult, setMatchResult] = useState(null);
    const [overdue, setOverdue] = useState([]);
    const [testEmailLoading, setTestEmailLoading] = useState(false);
    const [testEmailResult, setTestEmailResult] = useState(null);
    const [crStats, setCrStats] = useState(null);
    const [crClients, setCrClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [commissions, setCommissions] = useState(null);
    const [transitionResult, setTransitionResult] = useState(null);

    const loadData = useCallback(async () => {
        try {
            const [s, b, se, d, m, o] = await Promise.all([
                API.get('/admin/stats'), API.get('/buyers'),
                API.get('/sellers'), API.get('/deals'),
                API.get('/matching/suggestions'),
                API.get('/payments/overdue/all').catch(() => ({ data: [] }))
            ]);
            setStats(s.data); setBuyers(b.data); setSellers(se.data);
            setDeals(d.data); setMatches(m.data); setOverdue(o.data);
            try {
                const [crs, crc, comm] = await Promise.all([
                    API.get('/credit-repair/stats'),
                    API.get('/credit-repair/all-clients'),
                    API.get('/admin/commissions'),
                ]);
                setCrStats(crs.data);
                setCrClients(crc.data);
                setCommissions(comm.data);
            } catch (e) { /* may fail if no data */ }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const runMatching = async () => {
        setMatchLoading(true);
        try {
            const { data } = await API.post('/matching/run');
            setMatchResult(data);
            await loadData();
        } catch (err) { console.error(err); }
        finally { setMatchLoading(false); }
    };

    const sendTestEmail = async () => {
        setTestEmailLoading(true);
        try {
            const { data } = await API.post('/notifications/test-email');
            setTestEmailResult(data);
        } catch (err) { console.error(err); }
        finally { setTestEmailLoading(false); }
    };

    const sendPaymentReminder = async (dealId, monthNum) => {
        try {
            await API.post('/payments/send-reminder', { deal_id: dealId, month_number: monthNum });
            await loadData();
        } catch (err) { console.error(err); }
    };

    const approveMatch = async (buyerId, sellerId) => {
        try {
            await API.post('/matching/approve', { buyer_id: buyerId, seller_id: sellerId });
            await loadData();
        } catch (err) { console.error(err); }
    };

    const updateDealStatus = async (dealId, status) => {
        try {
            await API.put(`/deals/${dealId}/status`, { status });
            await loadData();
        } catch (err) { console.error(err); }
    };

    const markPaid = async (listingId) => {
        try {
            await API.post('/admin/commissions/mark-paid', { listing_id: listingId });
            await loadData();
        } catch (err) { console.error(err); }
    };

    const undoPayout = async (listingId) => {
        try {
            await API.post('/admin/commissions/undo-paid', { listing_id: listingId });
            await loadData();
        } catch (err) { console.error(err); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-[#F5F5F4]" data-testid="admin-page">
            <Navbar variant="light" />

            {/* ─── BRANDED HEADER ─── */}
            <div className="bg-zinc-900 pt-20 pb-8" data-testid="admin-branded-header">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center gap-4 mb-6">
                        <VisionAireLogo size={48} />
                        <div>
                            <h1 className="text-2xl font-semibold text-white font-['Outfit'] tracking-wide">Admin Dashboard</h1>
                            <p className="text-sm text-zinc-400 mt-0.5">VisionAire Realty Services — Command Center</p>
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                            <Button size="sm" variant="outline" onClick={async () => {
                                try {
                                    const { data } = await API.post('/autonomy/auto-transitions');
                                    setTransitionResult(data);
                                    if (data.count > 0) await loadData();
                                } catch (e) { console.error(e); }
                            }} data-testid="admin-auto-transitions-btn"
                                className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-lg text-xs">
                                <Zap className="w-3.5 h-3.5 mr-1.5" /> Auto-Advance{transitionResult?.count > 0 ? ` (${transitionResult.count})` : ''}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => navigate('/analytics')} data-testid="admin-go-analytics"
                                className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-lg text-xs">
                                <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Analytics
                            </Button>
                            <Button size="sm" onClick={() => navigate('/marketplace')} data-testid="admin-go-marketplace"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs">
                                <ArrowRight className="w-3.5 h-3.5 mr-1.5" /> Marketplace
                            </Button>
                        </div>
                    </div>

                    {/* ─── BRANDED STAT CARDS ─── */}
                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
                            <StatCard label="Buyers" value={stats.total_buyers} sub={`${stats.qualified_buyers} qualified`} accent="emerald" icon={Users} />
                            <StatCard label="Sellers" value={stats.total_sellers} accent="amber" icon={Home} />
                            <StatCard label="Active Deals" value={stats.active_deals} sub={`${stats.closed_deals} closed`} accent="sky" icon={FileText} />
                            <StatCard label="Payments" value={stats.total_payments} sub={`${stats.total_signatures} sigs`} accent="emerald" icon={DollarSign} />
                            <StatCard label="Match Rate" value={`${stats.match_rate}%`} accent="violet" icon={BarChart3} />
                        </div>
                    )}
                </div>
            </div>

            {/* ─── DAY 91 PIPELINE BAR ─── */}
            {stats && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-6 flex-wrap shadow-sm" data-testid="admin-day91-pipeline">
                        <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Day 91 Pipeline</span>
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-zinc-600">Pending: <strong className="text-amber-700">{stats.pending_listings || 0}</strong></span>
                            <span className="text-zinc-600">Active: <strong className="text-emerald-700">{stats.active_listings || 0}</strong></span>
                            <span className="text-zinc-600">Agents: <strong className="text-blue-700">{stats.total_agents || 0}</strong></span>
                            <span className="text-zinc-600">Operators: <strong className="text-purple-700">{stats.total_operators || 0}</strong></span>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── MAIN CONTENT ─── */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-12">
                <Tabs defaultValue="deals" className="space-y-6">
                    {/* Global Search & Filter */}
                    <div className="flex items-center gap-3 mb-2" data-testid="admin-search-bar">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <Input placeholder="Search buyers, sellers, deals, properties..."
                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                className="pl-9 border-zinc-200 h-9 text-sm" data-testid="admin-search-input" />
                        </div>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                            className="text-xs border border-zinc-200 rounded-lg px-3 py-2 bg-white" data-testid="admin-status-filter">
                            <option value="all">All Statuses</option>
                            <option value="lead">Lead</option>
                            <option value="matched">Matched</option>
                            <option value="negotiation">Negotiation</option>
                            <option value="finalized">Finalized</option>
                            <option value="closed">Closed</option>
                        </select>
                    </div>
                    <TabsList className="bg-white border border-zinc-200 shadow-sm rounded-xl p-1 flex-wrap">
                        <TabsTrigger value="deals" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-deals">
                            <FileText className="w-4 h-4 mr-1.5" /> Deals
                        </TabsTrigger>
                        <TabsTrigger value="buyers" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-buyers">
                            <Users className="w-4 h-4 mr-1.5" /> Buyers
                        </TabsTrigger>
                        <TabsTrigger value="sellers" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-sellers">
                            <Home className="w-4 h-4 mr-1.5" /> Sellers
                        </TabsTrigger>
                        <TabsTrigger value="matching" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-matching">
                            <Target className="w-4 h-4 mr-1.5" /> Matching
                        </TabsTrigger>
                        <TabsTrigger value="payments" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-payments">
                            <DollarSign className="w-4 h-4 mr-1.5" /> Payments
                        </TabsTrigger>
                        <TabsTrigger value="commissions" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-commissions">
                            <Briefcase className="w-4 h-4 mr-1.5" /> Commissions
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-notifications">
                            <Bell className="w-4 h-4 mr-1.5" /> Notifications
                        </TabsTrigger>
                        <TabsTrigger value="credit-repair" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-credit-repair">
                            <Heart className="w-4 h-4 mr-1.5" /> Credit Repair
                        </TabsTrigger>
                        <TabsTrigger value="templates" className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg text-xs" data-testid="admin-tab-templates">
                            <PenTool className="w-4 h-4 mr-1.5" /> Templates
                        </TabsTrigger>
                        <TabsTrigger value="pitch-deck" className="data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-900 rounded-lg text-xs font-semibold" data-testid="admin-tab-pitch-deck">
                            <Briefcase className="w-4 h-4 mr-1.5" /> Investor Deck
                        </TabsTrigger>
                        <TabsTrigger value="phone" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-lg text-xs font-semibold" data-testid="admin-tab-phone">
                            <Phone className="w-4 h-4 mr-1.5" /> Phone
                        </TabsTrigger>
                        <TabsTrigger value="appointments" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg text-xs font-semibold" data-testid="admin-tab-appointments">
                            <Calendar className="w-4 h-4 mr-1.5" /> Appointments
                        </TabsTrigger>
                        <TabsTrigger value="relocation" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white rounded-lg text-xs font-semibold" data-testid="admin-tab-relocation">
                            <Globe className="w-4 h-4 mr-1.5" /> Relocation
                        </TabsTrigger>
                        <TabsTrigger value="lead-radar" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-lg text-xs font-semibold" data-testid="admin-tab-lead-radar">
                            <Radar className="w-4 h-4 mr-1.5" /> Lead Radar
                        </TabsTrigger>
                    </TabsList>

                    {/* ── DEALS ── */}
                    <TabsContent value="deals">
                        {(() => {
                            const filtered = deals.filter(d => {
                                const matchSearch = !searchTerm || d.property_address?.toLowerCase().includes(searchTerm.toLowerCase()) || d.buyer_name?.toLowerCase().includes(searchTerm.toLowerCase());
                                const matchStatus = statusFilter === 'all' || d.status === statusFilter;
                                return matchSearch && matchStatus;
                            });
                            return filtered.length === 0 ? (
                            <EmptyState icon={FileText} title="No Deals" desc="Approve matches to create deals." />
                        ) : (
                            <div className="space-y-3">
                                <div className="text-xs text-zinc-500 mb-2">{filtered.length} deals found</div>
                                {filtered.map(d => (
                                    <div key={d.id} className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm hover:border-zinc-300 transition-colors">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <div className="text-sm font-medium text-zinc-900">{d.property_address}</div>
                                                <div className="text-xs text-zinc-500">Buyer: {d.buyer_name || d.buyer_user_id}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge className={STATUS_COLORS[d.status]}>{d.status}</Badge>
                                                <select data-testid={`deal-status-${d.id}`} value={d.status}
                                                    onChange={e => updateDealStatus(d.id, e.target.value)}
                                                    className="text-xs border border-zinc-200 rounded-lg px-2 py-1 bg-white">
                                                    {['lead', 'matched', 'negotiation', 'finalized', 'closed'].map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-4 gap-4 text-center text-sm">
                                            <div><span className="text-xs text-zinc-500">Down</span><div className="font-semibold">${(d.down_payment || 0).toLocaleString()}</div></div>
                                            <div><span className="text-xs text-zinc-500">Monthly</span><div className="font-semibold">${(d.monthly_payment || 0).toLocaleString()}</div></div>
                                            <div><span className="text-xs text-zinc-500">Rate</span><div className="font-semibold">{((d.interest_rate || 0) * 100).toFixed(1)}%</div></div>
                                            <div><span className="text-xs text-zinc-500">Balloon</span><div className="font-semibold">${(d.balloon_amount || 0).toLocaleString()}</div></div>
                                        </div>
                                        <div className="flex gap-2 mt-3">
                                            <Button size="sm" variant="outline" onClick={() => navigate(`/deals/${d.id}`)} data-testid={`deal-detail-${d.id}`}
                                                className="text-xs border-zinc-200">
                                                View Details <ArrowRight className="ml-1 w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                        })()}
                    </TabsContent>

                    {/* ── BUYERS ── */}
                    <TabsContent value="buyers">
                        {(() => {
                            const filtered = buyers.filter(b => !searchTerm || b.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || b.target_location?.toLowerCase().includes(searchTerm.toLowerCase()));
                            return filtered.length === 0 ? (
                            <EmptyState icon={Users} title="No Buyers" desc="Buyers will appear after completing intake." />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="text-xs text-zinc-500 mb-1 col-span-full">{filtered.length} buyers found</div>
                                {filtered.map((b, i) => (
                                    <div key={i} className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="text-sm font-medium text-zinc-900">{b.full_name}</div>
                                            <Badge className={b.status === 'pre_qualified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                                {b.qualification}
                                            </Badge>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div><span className="text-zinc-500">Income:</span> {b.income_range}</div>
                                            <div><span className="text-zinc-500">Credit:</span> {b.credit_score_range}</div>
                                            <div><span className="text-zinc-500">Down:</span> ${(b.down_payment_available || 0).toLocaleString()}</div>
                                            <div><span className="text-zinc-500">Monthly:</span> ${(b.desired_monthly_payment || 0).toLocaleString()}</div>
                                            <div><span className="text-zinc-500">Location:</span> {b.target_location}</div>
                                            <div><span className="text-zinc-500">Score:</span> {((b.readiness_score || 0) * 20).toFixed(0)}%</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                        })()}
                    </TabsContent>

                    {/* ── SELLERS ── */}
                    <TabsContent value="sellers">
                        {(() => {
                            const filtered = sellers.filter(s => !searchTerm || s.property_address?.toLowerCase().includes(searchTerm.toLowerCase()) || s.city?.toLowerCase().includes(searchTerm.toLowerCase()) || s.agent_name?.toLowerCase().includes(searchTerm.toLowerCase()));
                            return filtered.length === 0 ? (
                            <EmptyState icon={Home} title="No Sellers" desc="Sellers will appear after completing intake." />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="text-xs text-zinc-500 mb-1 col-span-full">{filtered.length} sellers found</div>
                                {filtered.map((s, i) => (
                                    <div key={i} className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="text-sm font-medium text-zinc-900">{s.property_address}</div>
                                            <Badge className={s.motivation_tag === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                                {s.motivation_tag}
                                            </Badge>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div><span className="text-zinc-500">Value:</span> ${(s.estimated_value || 0).toLocaleString()}</div>
                                            <div><span className="text-zinc-500">Asking:</span> ${(s.asking_price || 0).toLocaleString()}</div>
                                            <div><span className="text-zinc-500">Mortgage:</span> ${(s.remaining_mortgage || 0).toLocaleString()}</div>
                                            <div><span className="text-zinc-500">Income:</span> ${(s.projected_monthly_income || 0).toLocaleString()}/mo</div>
                                            <div><span className="text-zinc-500">DOM:</span> {s.days_on_market || s.time_on_market || '-'}</div>
                                            <div><span className="text-zinc-500">Status:</span> <span className={s.listing_status === 'active' ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'}>{s.listing_status || '-'}</span></div>
                                            {s.agent_name && <div className="col-span-2"><span className="text-zinc-500">Agent:</span> {s.agent_name} ({s.agent_brokerage || ''})</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                        })()}
                    </TabsContent>

                    {/* ── MATCHING ── */}
                    <TabsContent value="matching">
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Matching Engine</h3>
                                        <p className="text-sm text-zinc-500">Run the algorithm to find compatible buyer-seller pairs</p>
                                    </div>
                                    <Button onClick={runMatching} disabled={matchLoading} data-testid="run-matching-btn"
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6">
                                        {matchLoading ? 'Running...' : <><Play className="w-4 h-4 mr-2" /> Run Matching</>}
                                    </Button>
                                </div>
                                {matchResult && (
                                    <div className="bg-emerald-50 rounded-lg p-3 text-sm text-emerald-700">
                                        <CheckCircle className="w-4 h-4 inline mr-2" />
                                        {matchResult.matches_created} new matches created
                                    </div>
                                )}
                            </div>

                            {matches.length > 0 && (
                                <div className="space-y-3">
                                    <h3 className="text-sm font-medium text-zinc-700">Match Suggestions ({matches.length})</h3>
                                    {matches.map(m => (
                                        <div key={m.id} className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                            <div className="flex items-center justify-between mb-3">
                                                <div>
                                                    <div className="text-sm font-medium text-zinc-900">{m.property_address}</div>
                                                    <div className="text-xs text-zinc-500">Buyer: {m.buyer_name || m.buyer_email}</div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Badge className="bg-emerald-100 text-emerald-700 font-semibold">{m.match_score}%</Badge>
                                                    {m.status === 'suggested' && (
                                                        <Button size="sm" onClick={() => approveMatch(m.buyer_user_id, m.seller_user_id)}
                                                            data-testid={`approve-match-${m.id}`}
                                                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg">
                                                            <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                                        </Button>
                                                    )}
                                                    {m.status === 'approved' && (
                                                        <Badge className="bg-blue-100 text-blue-700">Approved</Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4 text-xs">
                                                <div><span className="text-zinc-500">Asking:</span> ${(m.asking_price || 0).toLocaleString()}</div>
                                                <div><span className="text-zinc-500">Budget:</span> ${(m.buyer_budget_high || 0).toLocaleString()}</div>
                                                <div><span className="text-zinc-500">Down:</span> ${(m.buyer_down_payment || 0).toLocaleString()}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* ── PAYMENTS ── */}
                    <TabsContent value="payments">
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">Overdue Payments</h3>
                                {overdue.length === 0 ? (
                                    <div className="text-center py-8">
                                        <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                                        <p className="text-sm text-zinc-500">No overdue payments. All accounts current.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {overdue.map((o, i) => (
                                            <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-red-50 border border-red-200">
                                                <div>
                                                    <div className="text-sm font-medium text-zinc-900">{o.property_address}</div>
                                                    <div className="text-xs text-zinc-500">Buyer: {o.buyer_name} | Month {o.month_number}</div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm font-bold text-red-600">${(o.amount_due || 0).toLocaleString()}</span>
                                                    <Button size="sm" variant="outline" onClick={() => sendPaymentReminder(o.deal_id, o.month_number)}
                                                        className="text-xs border-red-200 text-red-600 h-7" data-testid={`admin-remind-${o.deal_id}-${o.month_number}`}>
                                                        <Mail className="w-3 h-3 mr-1" /> Send Reminder
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => navigate(`/deals/${o.deal_id}`)}
                                                        className="text-xs border-zinc-200 h-7">
                                                        View Deal
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* ── COMMISSIONS ── */}
                    <TabsContent value="commissions">
                        <div className="space-y-6" data-testid="commissions-tab-content">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-zinc-900 rounded-xl p-5 text-white">
                                    <div className="text-xs text-zinc-400 mb-1">Total Agents</div>
                                    <div className="text-2xl font-semibold font-['Outfit']">{commissions?.total_agents || 0}</div>
                                </div>
                                <div className="bg-zinc-900 rounded-xl p-5 text-white">
                                    <div className="text-xs text-zinc-400 mb-1">Total Listings</div>
                                    <div className="text-2xl font-semibold font-['Outfit']">{commissions?.total_listings || 0}</div>
                                </div>
                                <div className="bg-emerald-700 rounded-xl p-5 text-white">
                                    <div className="text-xs text-emerald-200 mb-1">Paid Out</div>
                                    <div className="text-2xl font-semibold font-['Outfit']">${(commissions?.total_earned || 0).toLocaleString()}</div>
                                </div>
                                <div className="bg-amber-600 rounded-xl p-5 text-white">
                                    <div className="text-xs text-amber-100 mb-1">Unpaid</div>
                                    <div className="text-2xl font-semibold font-['Outfit']">${(commissions?.total_pending || 0).toLocaleString()}</div>
                                </div>
                            </div>

                            {/* Commission Table */}
                            {(!commissions?.commissions || commissions.commissions.length === 0) ? (
                                <EmptyState icon={Briefcase} title="No Commissions Yet" desc="Agent commissions appear when agents enroll listings." />
                            ) : (
                                <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
                                    <div className="px-5 py-4 border-b border-zinc-100">
                                        <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">Agent Commission Ledger</h3>
                                        <p className="text-xs text-zinc-500 mt-0.5">3% commission on asking price per enrolled listing. Mark paid to record payout.</p>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm" data-testid="commission-table">
                                            <thead>
                                                <tr className="bg-zinc-50 border-b border-zinc-200 text-xs text-zinc-500 uppercase tracking-wider">
                                                    <th className="text-left px-5 py-3 font-medium">Agent</th>
                                                    <th className="text-left px-5 py-3 font-medium">Property</th>
                                                    <th className="text-right px-5 py-3 font-medium">Asking Price</th>
                                                    <th className="text-right px-5 py-3 font-medium">Commission</th>
                                                    <th className="text-center px-5 py-3 font-medium">Listing</th>
                                                    <th className="text-center px-5 py-3 font-medium">Payout</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-zinc-100">
                                                {commissions.commissions.map((c, i) => (
                                                    <tr key={i} className={`transition-colors ${c.paid ? 'bg-emerald-50/40' : 'hover:bg-zinc-50'}`}>
                                                        <td className="px-5 py-3 text-zinc-900 font-medium">{c.agent_name}</td>
                                                        <td className="px-5 py-3 text-zinc-600">{c.property_address || '—'}</td>
                                                        <td className="px-5 py-3 text-right text-zinc-900">${(c.asking_price || 0).toLocaleString()}</td>
                                                        <td className="px-5 py-3 text-right font-semibold text-emerald-700">${(c.commission_amount || 0).toLocaleString()}</td>
                                                        <td className="px-5 py-3 text-center">
                                                            <Badge className={LISTING_STATUS_COLORS[c.listing_status] || 'bg-zinc-100 text-zinc-600'}>{c.listing_status}</Badge>
                                                        </td>
                                                        <td className="px-5 py-3 text-center">
                                                            {c.paid ? (
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                                                                        <span className="text-xs font-medium text-emerald-700">Paid</span>
                                                                    </div>
                                                                    <span className="text-[10px] text-zinc-400">{c.paid_at ? new Date(c.paid_at).toLocaleDateString() : ''}</span>
                                                                    {c.paid_by && <span className="text-[10px] text-zinc-400">by {c.paid_by}</span>}
                                                                    <button onClick={() => undoPayout(c.listing_id)}
                                                                        data-testid={`undo-paid-${c.listing_id}`}
                                                                        className="text-[10px] text-zinc-400 hover:text-red-500 flex items-center gap-0.5 mt-0.5 transition-colors">
                                                                        <Undo2 className="w-3 h-3" /> Undo
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <Button size="sm" variant="outline"
                                                                    onClick={() => markPaid(c.listing_id)}
                                                                    data-testid={`mark-paid-${c.listing_id}`}
                                                                    className="text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-7 rounded-lg">
                                                                    <DollarSign className="w-3 h-3 mr-1" /> Mark Paid
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* ── NOTIFICATIONS ── */}
                    <TabsContent value="notifications">
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-2">Email Notifications</h3>
                                <p className="text-sm text-zinc-500 mb-4">Powered by Brevo (Sendinblue). Emails are sent automatically on key events.</p>
                                <div className="space-y-3 mb-6">
                                    {[
                                        { event: "Deal Status Change", desc: "Buyer & seller notified when status moves in the pipeline", active: true },
                                        { event: "New Match Found", desc: "Both parties notified when the matching engine finds a pair", active: true },
                                        { event: "Payment Reminder", desc: "Buyer receives reminder for upcoming/overdue payments", active: true },
                                        { event: "Payment Received", desc: "Seller notified when a payment is recorded", active: true },
                                        { event: "E-Signature Complete", desc: "All parties notified when agreement is signed", active: true },
                                    ].map((n, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 border border-zinc-200">
                                            <div>
                                                <div className="text-sm font-medium text-zinc-900">{n.event}</div>
                                                <div className="text-xs text-zinc-500">{n.desc}</div>
                                            </div>
                                            <Badge className={n.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}>
                                                {n.active ? "Active" : "Inactive"}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center gap-3">
                                    <Button onClick={sendTestEmail} disabled={testEmailLoading} data-testid="test-email-btn"
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm">
                                        <Mail className="w-4 h-4 mr-2" />
                                        {testEmailLoading ? 'Sending...' : 'Send Test Email'}
                                    </Button>
                                    {testEmailResult && (
                                        <span className="text-sm text-emerald-600">
                                            <CheckCircle className="w-4 h-4 inline mr-1" />
                                            {testEmailResult.success ? 'Test email sent!' : 'Failed to send'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* ── CREDIT REPAIR ── */}
                    <TabsContent value="credit-repair">
                        <div className="space-y-6">
                            {crStats && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                        <div className="text-xs text-zinc-500 mb-1">Total Clients</div>
                                        <div className="text-2xl font-semibold text-zinc-900 font-['Outfit']">{crStats.total_clients}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                        <div className="text-xs text-zinc-500 mb-1">In Disputes</div>
                                        <div className="text-2xl font-semibold text-blue-700 font-['Outfit']">{crStats.in_disputes}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                        <div className="text-xs text-zinc-500 mb-1">Graduated</div>
                                        <div className="text-2xl font-semibold text-emerald-700 font-['Outfit']">{crStats.graduated}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                        <div className="text-xs text-zinc-500 mb-1">Dispute Resolution</div>
                                        <div className="text-2xl font-semibold text-amber-700 font-['Outfit']">{crStats.resolution_rate}%</div>
                                    </div>
                                </div>
                            )}
                            {crClients.length === 0 ? (
                                <EmptyState icon={Heart} title="No Credit Repair Clients" desc="Clients enroll through the Credit Repair page." />
                            ) : (
                                <div className="space-y-3">
                                    {crClients.map(c => (
                                        <div key={c.id} className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <div>
                                                    <div className="text-sm font-medium text-zinc-900">{c.full_name}</div>
                                                    <div className="text-xs text-zinc-500">{c.email} | {c.phone}</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge className="bg-emerald-100 text-emerald-700">{c.program_tier}</Badge>
                                                    <Badge className="bg-blue-100 text-blue-700">{c.program_phase}</Badge>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4 text-xs text-center">
                                                <div><span className="text-zinc-500">Score</span><div className="font-semibold text-zinc-900">{c.current_score_range}</div></div>
                                                <div><span className="text-zinc-500">Target</span><div className="font-semibold text-zinc-900">{c.target_score}</div></div>
                                                <div><span className="text-zinc-500">Items</span><div className="font-semibold text-zinc-900">{(c.negative_items || []).length}</div></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* ── TEMPLATES ── */}
                    <TabsContent value="templates">
                        <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm text-center">
                            <PenTool className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                            <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">Document Templates</h3>
                            <p className="text-sm text-zinc-500 mb-4">Create and manage document templates for deals, agreements, and disclosures.</p>
                            <Button onClick={() => navigate('/templates')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="admin-goto-templates-btn">
                                <FileText className="w-4 h-4 mr-2" /> Manage Templates <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                        </div>
                    </TabsContent>

                    {/* ── INVESTOR PITCH DECK ── */}
                    <TabsContent value="pitch-deck">
                        <PitchDeckTab />
                    </TabsContent>

                    {/* ── PHONE DIALER ── */}
                    <TabsContent value="phone">
                        <PhoneDialerTab />
                    </TabsContent>

                    {/* ── APPOINTMENTS ── */}
                    <TabsContent value="appointments">
                        <AppointmentsTab />
                    </TabsContent>

                    {/* ── RELOCATION LEADS ── */}
                    <TabsContent value="relocation">
                        <RelocationLeadsTab />
                    </TabsContent>

                    {/* ── LEAD RADAR ── */}
                    <TabsContent value="lead-radar">
                        <LeadRadarTab />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function StatCard({ label, value, sub, accent, icon: Icon }) {
    const bgMap = {
        emerald: 'bg-emerald-600', amber: 'bg-amber-500', sky: 'bg-sky-600', violet: 'bg-violet-600',
    };
    const iconBg = bgMap[accent] || 'bg-zinc-600';
    return (
        <div className="bg-zinc-800 rounded-xl p-4 flex items-start gap-3 border border-zinc-700/50" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className={`${iconBg} w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-4.5 h-4.5 text-white" />
            </div>
            <div className="min-w-0">
                <div className="text-xl font-semibold text-white font-['Outfit'] leading-tight">{value}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{label}</div>
                {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
            </div>
        </div>
    );
}

function EmptyState({ icon: Icon, title, desc }) {
    return (
        <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
            <Icon className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">{title}</h3>
            <p className="text-sm text-zinc-500">{desc}</p>
        </div>
    );
}

function PitchDeckTab() {
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState({
        company_tagline: "When Traditional Real Estate Fails, We Create Opportunity",
        total_users: "150+",
        active_credit_clients: "45",
        deals_in_pipeline: "12",
        deals_closed: "3",
        properties_listed: "28",
        monthly_revenue: "$8,200",
        investment_ask: "$500,000",
        use_of_funds: "Platform scaling (40%), Marketing (30%), Team (20%), Operations (10%)",
        year1_revenue: "$240,000",
        year2_revenue: "$1,200,000",
        year3_revenue: "$4,800,000",
        founder_name: "",
        founder_title: "Founder & CEO",
        founder_bio: "",
        team_members: "",
        contact_email: "invest@visionairerealty.com",
        contact_phone: "",
    });

    useEffect(() => {
        API.get('/admin/pitch-deck/defaults').then(r => {
            setFields(prev => ({ ...prev, ...r.data }));
        }).catch(() => {});
    }, []);

    const update = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

    const generate = async () => {
        setLoading(true);
        try {
            const response = await API.post('/admin/pitch-deck/generate', fields, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `VisionAire_Investor_Deck_${new Date().toISOString().slice(0, 10)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { console.error('Deck generation failed:', err); }
        finally { setLoading(false); }
    };

    const Section = ({ title, children }) => (
        <div className="mb-6">
            <h4 className="text-sm font-semibold text-amber-600 uppercase tracking-wide mb-3 font-['Outfit']">{title}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
        </div>
    );

    const Field = ({ label, fieldKey, placeholder, full }) => (
        <div className={full ? 'md:col-span-2' : ''}>
            <label className="block text-xs font-medium text-zinc-600 mb-1">{label}</label>
            <Input
                value={fields[fieldKey] || ''}
                onChange={(e) => update(fieldKey, e.target.value)}
                placeholder={placeholder}
                className="rounded-lg text-sm"
                data-testid={`pitch-${fieldKey}`}
            />
        </div>
    );

    return (
        <div className="space-y-4" data-testid="pitch-deck-tab">
            <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl border border-zinc-700 p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-white font-['Outfit'] flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-amber-400" /> Investor Pitch Deck Generator
                        </h3>
                        <p className="text-xs text-zinc-400 mt-1">Customize every field below, then generate a professional 12-slide PDF deck</p>
                    </div>
                    <Button
                        onClick={generate}
                        disabled={loading}
                        className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold rounded-xl px-6"
                        data-testid="generate-pitch-deck-btn"
                    >
                        {loading ? 'Generating...' : 'Generate & Download PDF'}
                    </Button>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
                <Section title="Branding">
                    <Field label="Company Tagline" fieldKey="company_tagline" placeholder="Your company tagline" full />
                </Section>

                <Section title="Traction & Metrics (Slide 7)">
                    <Field label="Registered Users" fieldKey="total_users" placeholder="e.g. 150+" />
                    <Field label="Active Credit Repair Clients" fieldKey="active_credit_clients" placeholder="e.g. 45" />
                    <Field label="Deals in Pipeline" fieldKey="deals_in_pipeline" placeholder="e.g. 12" />
                    <Field label="Deals Closed" fieldKey="deals_closed" placeholder="e.g. 3" />
                    <Field label="Properties Listed" fieldKey="properties_listed" placeholder="e.g. 28" />
                    <Field label="Monthly Revenue" fieldKey="monthly_revenue" placeholder="e.g. $8,200" />
                </Section>

                <Section title="Financial Projections (Slide 11)">
                    <Field label="Year 1 Revenue" fieldKey="year1_revenue" placeholder="e.g. $240,000" />
                    <Field label="Year 2 Revenue" fieldKey="year2_revenue" placeholder="e.g. $1,200,000" />
                    <Field label="Year 3 Revenue" fieldKey="year3_revenue" placeholder="e.g. $4,800,000" />
                </Section>

                <Section title="The Ask (Slide 12)">
                    <Field label="Investment Amount" fieldKey="investment_ask" placeholder="e.g. $500,000" />
                    <Field label="Use of Funds" fieldKey="use_of_funds" placeholder="Platform scaling (40%), Marketing (30%)..." full />
                </Section>

                <Section title="Team (Slide 10)">
                    <Field label="Founder Name" fieldKey="founder_name" placeholder="Your name" />
                    <Field label="Founder Title" fieldKey="founder_title" placeholder="Founder & CEO" />
                    <Field label="Founder Bio" fieldKey="founder_bio" placeholder="Brief professional bio..." full />
                    <Field label="Team Members (separate with |)" fieldKey="team_members" placeholder="Jane Doe, CTO | John Smith, Advisor" full />
                </Section>

                <Section title="Contact">
                    <Field label="Contact Email" fieldKey="contact_email" placeholder="invest@visionairerealty.com" />
                    <Field label="Contact Phone" fieldKey="contact_phone" placeholder="(713) 555-0000" />
                </Section>

                <div className="flex justify-end mt-4">
                    <Button
                        onClick={generate}
                        disabled={loading}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white font-semibold rounded-xl px-8"
                        data-testid="generate-pitch-deck-btn-bottom"
                    >
                        {loading ? 'Generating...' : 'Generate & Download PDF'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

